import { contactUsRoutes } from '@/backend/config/routes';
import { EnvironmentConfig } from '@/backend/types/extraTypes';
import { DialogHelper } from '@juvare/common-web-ui/DialogHelper';
import { LandingApp } from '@juvare/common-web-ui/LandingApp';
import { SnackbarHelper } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateCustom, getErrorMessage } from '@juvare/common-web-ui/utils';
import { ContactUsPanel, afterSubmitDialog } from '@juvare/contact-management/ui/features/contact-us';
import { PublicSelfRegistration } from '@juvare/contact-management/ui/features/registration';
import { useContactManagementTranslation } from '@juvare/contact-management/ui/hooks/useContactManagementTranslation';
import { EditablePageData } from '@juvare/platform-sdk-react/backend/features/editable-pages/shared';
import { getPublicSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { getLoginUrl } from '@juvare/platform-sdk-react/ui/utils';
import { Alert, Box, LinearProgress } from '@mui/material';
import { marked } from 'marked';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import landingPageImage from './assets/images/landingPageImage.svg';
import { FormConfigContextProvider } from './features/forms/providers/FormConfigContextProvider';
import { useEnvironmentConfig } from './hooks/useEnvironmentConfig';
import { ContactManagementConfigProviderPublic } from './providers/ContactManagementConfigProviderPublic';
import { appName, baseName } from './utils/utils';

interface Props {
    config: EnvironmentConfig;
    pages: EditablePageData[];
}

export const Anonymous: React.FC<Props> = (props) => {
    const { t } = useTranslation('healthAlerts');
    const { t: tContactManagement } = useContactManagementTranslation();
    const { data: environmentConfig, error: environmentConfigError } = useEnvironmentConfig();
    const loginPage = props.pages.filter((page) => page.tags.includes('login'))[0];
    const publicSettings = useSelector(getPublicSettings);
    const contactUsPage = {
        path: contactUsRoutes.root,
        label: tContactManagement('contactUs.title'),
        customElement: (
            <ContactUsPanel
                isAnonymous
                appName={appName}
                resetFormAfterSubmit
                onAfterSubmit={(data) => afterSubmitDialog(tContactManagement, data)}
            />
        ),
    };
    const allPages = [
        ...props.pages
            .filter((page) => page.tags.includes('landingPage'))
            .map((page) => ({
                path: page.url,
                label: page.label,
                title: page.title,
                htmlContent: page.content ? (marked(page.content) as string) : '',
            })),
    ];

    if (environmentConfig === undefined) {
        return <LinearProgress />;
    } else if (environmentConfigError) {
        return <Alert security='error'>{getErrorMessage(environmentConfigError)}</Alert>;
    }

    return (
        <ContactManagementConfigProviderPublic>
            <FormConfigContextProvider googleRecaptchaV2SiteKey={environmentConfig?.googleRecaptchaV2SiteKey}>
                <DialogHelper />
                <SnackbarHelper />
                <LandingApp
                    appTitle={publicSettings.appTitle}
                    image={landingPageImage}
                    appImage={
                        publicSettings.showClientLogo ? (
                            <Box component='img' src={publicSettings.clientLogo} sx={{ maxWidth: '180px', maxHeight: '60px' }} />
                        ) : undefined
                    }
                    loginTitle={loginPage?.title ?? loginPage?.label ?? ''}
                    loginHtmlContent={marked(loginPage?.content ?? '') as string}
                    oktaLoginFormProps={{
                        oktaUrl: props.config.oktaUrl,
                        onLogin: () => {
                            window.sessionStorage.setItem('redirectUrl', location.pathname);
                            window.location.assign(getLoginUrl());
                        },
                        onRegister: publicSettings.allowSelfRegister ? (navigate) => navigate('/registration') : undefined,
                    }}
                    pages={publicSettings.enableContactUs.enabled ? [...allPages, contactUsPage] : allPages}
                    routes={[
                        {
                            path: 'registration',
                            element: <PublicSelfRegistration app={appName} />,
                        },
                    ]}
                    basename={baseName}
                    footerCopyright={t('footer.message', { copyrightYear: formatDateCustom(new Date(__BUILD_TIMESTAMP__), 'yyyy') })}
                />
            </FormConfigContextProvider>
        </ContactManagementConfigProviderPublic>
    );
};
